// src/pages/app/Punch.js
import { useEffect, useState, useCallback } from "react";
import {
  auth,
  getOpenPunch,
  startPunch,
  endPunch,
  punchEntriesRef,
} from "../../lib/firebase";
import { query, orderBy, limit, getDocs } from "firebase/firestore";

export default function Punch() {
  const [openPunch, setOpenPunch] = useState(null); // { id, open, startTime, endTime, ... }
  const [loading, setLoading] = useState(true);
  const [recent, setRecent] = useState([]);
  const [err, setErr] = useState("");

  const uid = auth.currentUser?.uid;

  const loadOpen = useCallback(async () => {
    if (!uid) return;
    const p = await getOpenPunch(uid);
    setOpenPunch(p);
  }, [uid]);

  const loadRecent = useCallback(async () => {
    if (!uid) return;

    // ✅ Model B only: punches/{uid}/entries
    // No uid filter needed because path already scopes to user
    const qy = query(
      punchEntriesRef(uid),
      orderBy("startTime", "desc"),
      limit(10)
    );

    const s = await getDocs(qy);
    setRecent(s.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, [uid]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setErr("");
      try {
        await Promise.all([loadOpen(), loadRecent()]);
      } catch (e) {
        console.error("Punch load error:", e);
        if (!cancelled) {
          setErr(e?.code ? `${e.code}: ${e.message}` : "Could not load punch data.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadOpen, loadRecent]);

  const punchedIn = !!openPunch?.open;

  async function handleClockIn() {
    try {
      setErr("");
      if (!uid) throw new Error("Not signed in.");

      // ✅ Model B start
      await startPunch(uid);

      await Promise.all([loadOpen(), loadRecent()]);
    } catch (e) {
      console.error("Clock-in failed:", e);
      setErr(e?.code ? `${e.code}: ${e.message}` : "Clock-in failed.");
    }
  }

  async function handleClockOut() {
    try {
      setErr("");
      if (!uid) throw new Error("Not signed in.");

      // ✅ Model B end
      const closed = await endPunch(uid);
      if (!closed) {
        setErr("You are not currently punched in.");
        return;
      }

      await Promise.all([loadOpen(), loadRecent()]);
    } catch (e) {
      console.error("Clock-out failed:", e);
      setErr(e?.code ? `${e.code}: ${e.message}` : "Clock-out failed.");
    }
  }

  if (!uid) return <div>Please sign in.</div>;
  if (loading) return <div>Loading status…</div>;
  if (err) return <div style={{ color: "#b91c1c" }}>{err}</div>;

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">Punch In / Out</h1>

      {punchedIn ? (
        <div className="space-y-3">
          <div className="p-4 rounded border bg-green-50">
            Currently <strong>Punched In</strong>
          </div>
          <button
            onClick={handleClockOut}
            className="px-4 py-2 rounded bg-gray-900 text-white"
          >
            Punch Out
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="p-4 rounded border bg-yellow-50">
            Currently <strong>Punched Out</strong>
          </div>
          <button
            onClick={handleClockIn}
            className="px-4 py-2 rounded bg-gray-900 text-white"
          >
            Punch In
          </button>
        </div>
      )}

      <h2 className="text-lg font-semibold mt-6 mb-2">Recent Punches</h2>

      <ul className="space-y-1 text-sm">
        {recent.map((r) => {
          const start =
            r.startTime?.toDate?.() ||
            (r.startTime?.seconds ? new Date(r.startTime.seconds * 1000) : null);

          const end =
            r.endTime?.toDate?.() ||
            (r.endTime?.seconds ? new Date(r.endTime.seconds * 1000) : null);

          const status = r.open ? "IN" : "OUT";
          const when = start ? start.toLocaleString() : "—";
          const endTxt = end ? ` → ${end.toLocaleString()}` : "";

          return (
            <li key={r.id}>
              {status}: {when}
              {endTxt}
            </li>
          );
        })}
      </ul>
    </div>
  );
}